import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ExternalLink, CheckCircle2, AlertCircle, Loader2, ShieldCheck } from "lucide-react";

/**
 * StripeConnectPanel — onboarding + status panel for owner's Stripe Express account.
 * The Stripe-hosted onboarding URL opens in a new tab; we poll the connect status
 * to detect when the owner finishes (or refreshes the page after returning).
 */
export default function StripeConnectPanel() {
  const { user, setUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/me/stripe/connect/status");
      setStatus(data);
      // Reflect on local user
      if (setUser && user) setUser({ ...user, ...data });
    } catch (err) {
      // not a blocker
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  // If the user just returned from Stripe (?stripe_return=1), poll status a few times
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (url.searchParams.get("stripe_return") === "1" || url.searchParams.get("stripe_refresh") === "1") {
      url.searchParams.delete("stripe_return");
      url.searchParams.delete("stripe_refresh");
      window.history.replaceState({}, "", url.toString());
      let n = 0;
      const t = setInterval(() => {
        refresh();
        n += 1;
        if (n >= 4) clearInterval(t);
      }, 1500);
      return () => clearInterval(t);
    }
  }, []);

  const onConnect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/stripe/connect/onboard", { origin_url: window.location.origin });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur Stripe");
      setBusy(false);
    }
  };

  const openDashboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/stripe/connect/login-link", {});
      if (data?.url) window.open(data.url, "_blank", "noopener");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur Stripe");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="border border-black/10 bg-white p-6 text-sm text-neutral-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement de votre compte Stripe…</div>;
  }

  const connected = !!status?.stripe_account_id;
  const ready = !!status?.stripe_charges_enabled && !!status?.stripe_payouts_enabled;

  return (
    <div className="border border-black/10 bg-white p-6" data-testid="stripe-connect-panel">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-[#635BFF] text-white flex items-center justify-center rounded-md shrink-0">
          <ShieldCheck className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl font-bold">Compte de paiement Stripe</div>
          <p className="text-sm text-neutral-600 mt-1">
            Reliez votre compte bancaire via Stripe Express : <strong>nuitée, frais de ménage et taxe de séjour</strong> sont versés directement chez vous.
            La commission MSB de 10% est prélevée automatiquement.
          </p>

          {/* Statut */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <StatusPill ok={connected} ko="Non connecté" label="Compte créé" testid="cnx-account" />
            <StatusPill ok={status?.stripe_details_submitted} ko="Informations manquantes" label="Onboarding" testid="cnx-details" />
            <StatusPill ok={ready} ko="Encaissement bloqué" label="Encaissements actifs" testid="cnx-charges" />
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            {!connected && (
              <button
                onClick={onConnect}
                disabled={busy}
                data-testid="stripe-connect-btn"
                className="bg-[#635BFF] text-white px-5 py-3 font-semibold hover:bg-[#4F46E5] flex items-center gap-2 disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                Connecter mon compte Stripe
              </button>
            )}
            {connected && !ready && (
              <button
                onClick={onConnect}
                disabled={busy}
                data-testid="stripe-continue-btn"
                className="bg-[#0A0A0A] text-white px-5 py-3 font-semibold hover:bg-[#1B2A49] flex items-center gap-2 disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                Finaliser l'onboarding Stripe
              </button>
            )}
            {connected && (
              <button
                onClick={openDashboard}
                disabled={busy}
                data-testid="stripe-dashboard-btn"
                className="border-2 border-[#0A0A0A] px-5 py-3 font-semibold hover:bg-neutral-100 flex items-center gap-2 disabled:opacity-60"
              >
                <ExternalLink className="w-4 h-4" /> Mon tableau de bord Stripe
              </button>
            )}
            <button
              onClick={refresh}
              className="text-sm underline underline-offset-4"
              data-testid="stripe-refresh-btn"
            >
              Rafraîchir le statut
            </button>
          </div>

          {!ready && (
            <div className="mt-4 p-3 bg-[#FFFBEA] border border-[#C9A14A]/40 text-xs text-neutral-700">
              ⚠️ Tant que votre compte Stripe n'est pas finalisé, vos annonces resteront en <strong>brouillon</strong> — les voyageurs ne pourront pas réserver.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ ok, label, ko, testid }) {
  return (
    <div className="flex items-center gap-2 border border-black/10 p-3" data-testid={testid}>
      {ok
        ? <CheckCircle2 className="w-4 h-4 text-[#166534] shrink-0" />
        : <AlertCircle className="w-4 h-4 text-[#9A4D1F] shrink-0" />}
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">{label}</div>
        <div className="text-xs font-semibold">{ok ? "OK" : ko}</div>
      </div>
    </div>
  );
}
